import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import isEmpty from 'lodash/isEmpty'
import { stringifyQuery } from 'utils/strings'

import {
  useDeleteTicketProviderTicketsMutation,
  useReloadTicketProviderTicketMutation,
} from 'api/ticketProviderTickets'

import * as sessionUserSelector from 'selectors/sessionUser'
import {
  RouteTicketProviderTickets,
  RouteAppRequests,
  RouteAdminWebhookEvents,
} from 'routes'

import { confirm } from 'components/Shared/ConfirmDialog/Index'
import ActionButtons from 'components/Shared/Skeleton/ActionButtons/Index'
import ReloadLink from './ReloadLink'
import RequestLink from './RequestLink'
import DeleteTicketLink from './DeleteTicketLink'
import WebhookLink from './WebhookLink'

const ButtonsToolbar = ({ ticket }) => {
  const { t } = useTranslation()
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)
  const userIsPower = useSelector(sessionUserSelector.isPowerUser)

  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const navigate = useNavigate()

  const [deleteTicketProviderTickets] = useDeleteTicketProviderTicketsMutation()
  const [reloadTicketProviderTicket] = useReloadTicketProviderTicketMutation()

  const onReloadClick = () => {
    reloadTicketProviderTicket({
      ticketProviderId: ticket.ticketProviderId,
      ticketId: ticket.externalId,
    })
  }

  const onDeleteClick = () => {
    const title = t('ticketProviderTicketPage.removeTicketTitle')
    const message = t('ticketProviderTicketPage.removeTicketMessage',
      {
        number: ticket.externalId,
      }
    )

    confirm({
      title: title,
      message: message,
    }).then(
      () => {
        deleteTicketProviderTickets({
          ticketProviderId: ticket.ticketProviderId,
          ticketIds: [ticket.id],
        }).then(fulfilled => {
          if (!fulfilled.data?.error) {
            navigate({
              pathname: RouteTicketProviderTickets({ ticketProviderId: ticket.ticketProviderId }),
            })
          }
        })
      },
      () => {}
    )
  }

  if (isEmpty(ticket) || !isUserPrivileged) {
    return null
  }

  const adminAppRequestsLink = {
    pathname: `${RouteAppRequests()}?${stringifyQuery({ filters: { ticket_id: ticket.externalId }, page: 1 })}`,
  }

  const adminWebhookEventsLink = {
    pathname: `${RouteAdminWebhookEvents()}?${stringifyQuery({ filters: { ticket_number: ticket.externalId }, page: 1 })}`,
  }

  const showReloadLink = ticket.canBeReloaded && !userIsPower
  const showWebhookLink =
    Boolean(process.env.ENABLE_TS_WEBHOOKS === 'true') && ticket.enableWebhookEvents && !userIsPower

  return (
    <ActionButtons>
      {showReloadLink && <ReloadLink onClick={onReloadClick} />}
      {userIsAdmin && <DeleteTicketLink onClick={onDeleteClick} />}
      <RequestLink to={adminAppRequestsLink} />
      {showWebhookLink && <WebhookLink to={adminWebhookEventsLink} />}
    </ActionButtons>
  )
}

ButtonsToolbar.propTypes = {
  ticket: PropTypes.object,
}

export default ButtonsToolbar
